import { useState, useCallback, useMemo, memo } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  Platform,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { FL, FONT, label, body, nums, ACCENT_GRADIENT } from "../floodlight";

// Floodlight (v1.71): a clear column inside the Community glass panel. A row
// brightens on a white wash under the pointer; the open channel sits on a
// purple wash with a soft purple edge.

const SIDEBAR_W = 232;

// ─── Sub-components ───────────────────────────────────────────────────────────

const CategoryRow = memo(function CategoryRow({ category, collapsed, onToggle }) {
  const handlePress = useCallback(() => onToggle(category.id), [onToggle, category.id]);
  return (
    <TouchableOpacity
      style={styles.categoryRow}
      onPress={handlePress}
      activeOpacity={0.7}
      accessibilityRole="button"
      aria-expanded={!collapsed}
      accessibilityLabel={`${category.label} channels`}
      dataSet={{ flLink: "" }}
    >
      <Ionicons
        name={collapsed ? "chevron-forward" : "chevron-down"}
        size={12}
        color={FL.muted}
        style={{ marginRight: 6 }}
      />
      <Text style={styles.categoryLabel} numberOfLines={1}>
        {category.label}
      </Text>
    </TouchableOpacity>
  );
});

const ChannelRow = memo(function ChannelRow({ channel, selected, onPress }) {
  const isActive = selected === channel.id;
  const fg = FL.ink;

  return (
    <TouchableOpacity
      style={[styles.channelRow, isActive && styles.channelRowActive]}
      dataSet={{ flRow: "" }}
      onPress={() => onPress(channel.id)}
      activeOpacity={0.85}
      accessibilityRole="button"
      aria-selected={!!isActive}
      accessibilityLabel={`Channel ${channel.displayName || channel.name || channel.id}`}
    >
      <View style={styles.channelIcon} dataSet={{ flLit: "" }}>
        {channel.type === "voice" ? (
          <Ionicons name="volume-medium-outline" size={16} color={fg} />
        ) : channel.locked ? (
          <Ionicons name="lock-closed-outline" size={14} color={fg} />
        ) : (
          <Text style={[styles.hashIcon, { color: isActive ? FL.greenOnInk : FL.muted }]}>#</Text>
        )}
      </View>

      <Text
        style={[styles.channelName, { color: fg, fontWeight: isActive ? "800" : "600" }]}
        numberOfLines={1}
        dataSet={{ flRowBody: "", flLit: "" }}
      >
        {channel.displayName ?? channel.name}
      </Text>

      <View style={styles.channelRight}>
        {!!channel.badge && !isActive && (
          <View style={styles.badge}>
            <Text style={[styles.badgeText, nums]}>{channel.badge > 99 ? "99+" : channel.badge}</Text>
          </View>
        )}
        {channel.type === "voice" && channel.members > 0 && (
          <View style={styles.voicePill} dataSet={{ flLit: "" }}>
            <Ionicons name="people-outline" size={11} color={fg} />
            <Text style={[styles.voiceCount, { color: fg }, nums]}>{channel.members}</Text>
          </View>
        )}
      </View>
    </TouchableOpacity>
  );
});

const SubChannelRow = memo(function SubChannelRow({ subChannel, selected, onPress }) {
  const isActive = selected === subChannel.id;
  const fg = isActive ? FL.ink : FL.ink2;

  return (
    <TouchableOpacity
      style={[styles.channelRow, styles.subRow, isActive && styles.channelRowActive]}
      dataSet={{ flRow: "" }}
      onPress={() => onPress(subChannel.id)}
      activeOpacity={0.85}
      accessibilityRole="button"
      aria-selected={!!isActive}
      accessibilityLabel={`Channel ${subChannel.displayName || subChannel.name || subChannel.id}`}
    >
      <View style={styles.channelIcon} dataSet={{ flLit: "" }}>
        <Text style={[styles.hashIcon, { color: isActive ? FL.greenOnInk : FL.muted, fontSize: 14 }]}>#</Text>
      </View>
      <Text style={[styles.channelName, { color: fg, fontSize: 13 }]} numberOfLines={1} dataSet={{ flRowBody: "", flLit: "" }}>
        {subChannel.displayName ?? subChannel.name}
      </Text>
    </TouchableOpacity>
  );
});

// ─── Main component ───────────────────────────────────────────────────────────

export default function ChannelsSidebar({
  serverName = "BetOptimized",
  categories: categoriesProp,
  loading = false,
  selectedChannelId = null,
  onChannelSelect,
  error = null,
  style,
}) {
  const [localSelected, setLocalSelected] = useState(null);
  const [collapsed, setCollapsed] = useState({});

  const data = useMemo(
    () =>
      categoriesProp?.length
        ? categoriesProp.map((c) => ({ ...c, label: c.label ?? c.name ?? c.id }))
        : loading ? [] : [],
    [categoriesProp, loading],
  );

  const toggleCategory = useCallback((id) => {
    setCollapsed((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const handleChannelPress = useCallback((id) => {
    setLocalSelected(id);
    onChannelSelect?.(id);
  }, [onChannelSelect]);

  const activeId = selectedChannelId ?? localSelected;

  return (
    <View style={[styles.sidebar, style]} accessibilityRole="navigation" accessibilityLabel="Channels">
      <View style={styles.serverHeader}>
        <View style={styles.serverLogo}>
          <Text style={styles.serverLogoText}>b°</Text>
        </View>
        <Text style={styles.serverName} numberOfLines={1}>{serverName}</Text>
      </View>

      <View style={styles.divider} />

      {loading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="small" color={FL.ink} />
        </View>
      ) : error || !data.length ? (
        <View style={styles.loadingContainer}>
          <Text style={[body(13), { color: FL.ink2, textAlign: "center", paddingHorizontal: 16 }]}>
            {error ? "Channels couldn't load. Sign in, or try again in a moment." : "No channels yet."}
          </Text>
        </View>
      ) : (
        <ScrollView
          style={styles.channelList}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{ paddingBottom: 16 }}
        >
          {data.map((category) => (
            <View key={category.id}>
              <CategoryRow
                category={category}
                collapsed={!!collapsed[category.id]}
                onToggle={toggleCategory}
              />
              {!collapsed[category.id] &&
                (category.channels ?? []).map((channel) => (
                  <View key={channel.id}>
                    <ChannelRow
                      channel={channel}
                      selected={activeId}
                      onPress={handleChannelPress}
                    />
                    {(channel.subChannels ?? []).map((sub) => (
                      <SubChannelRow
                        key={sub.id}
                        subChannel={sub}
                        selected={activeId}
                        onPress={handleChannelPress}
                      />
                    ))}
                  </View>
                ))}
            </View>
          ))}
        </ScrollView>
      )}
    </View>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const WEB = Platform.OS === "web";

const styles = StyleSheet.create({
  sidebar: {
    width: SIDEBAR_W,
    height: "100%",
    backgroundColor: "transparent",
    borderRightWidth: 1,
    borderRightColor: FL.hair,
    overflow: "hidden",
    flexDirection: "column",
  },

  serverHeader: { flexDirection: "row", alignItems: "center", paddingHorizontal: 14, height: 60, gap: 10 },
  serverLogo: {
    width: 30, height: 30, borderRadius: 10, backgroundColor: FL.green,
    ...(WEB ? { backgroundImage: ACCENT_GRADIENT } : {}),
    alignItems: "center", justifyContent: "center",
  },
  serverLogoText: { color: FL.onGreen, fontFamily: FONT, fontSize: 14, fontWeight: "900" },
  serverName: { flex: 1, color: FL.ink, fontFamily: FONT, fontSize: 13, fontWeight: "800", letterSpacing: 1.2, textTransform: "uppercase" },

  divider: { height: 1, backgroundColor: FL.hair },

  channelList: { flex: 1, paddingTop: 4 },
  loadingContainer: { flex: 1, alignItems: "center", justifyContent: "center" },

  categoryRow: {
    flexDirection: "row", alignItems: "center", alignSelf: "flex-start",
    marginHorizontal: 14, marginTop: 16, marginBottom: 6, minHeight: 24,
    ...(WEB ? { cursor: "pointer" } : {}),
  },
  categoryLabel: { ...label(11), color: FL.muted, fontWeight: "700", letterSpacing: 1.6 },

  channelRow: {
    flexDirection: "row", alignItems: "center", paddingHorizontal: 10, marginHorizontal: 8, marginVertical: 1,
    minHeight: 40, borderRadius: 10, borderWidth: 1, borderColor: "transparent",
    ...(WEB ? { cursor: "pointer" } : {}),
  },
  channelRowActive: { backgroundColor: FL.greenWash, borderColor: "rgba(162,89,255,0.35)" },
  subRow: { marginLeft: 26, minHeight: 36 },

  channelIcon: { width: 20, alignItems: "center", marginRight: 8 },
  hashIcon: { fontFamily: FONT, fontSize: 18, fontWeight: "800", lineHeight: 20 },
  channelName: { flex: 1, fontFamily: FONT, fontSize: 14, lineHeight: 18 },
  channelRight: { flexDirection: "row", alignItems: "center", gap: 4 },
  badge: { backgroundColor: FL.green, minWidth: 20, height: 20, borderRadius: 999, paddingHorizontal: 6, alignItems: "center", justifyContent: "center" },
  badgeText: { color: FL.onGreen, fontFamily: FONT, fontSize: 11, fontWeight: "800" },
  voicePill: { flexDirection: "row", alignItems: "center", gap: 3 },
  voiceCount: { fontFamily: FONT, fontSize: 11, fontWeight: "700" },
});
